import React, { useState } from 'react';
import {
    DollarSign, User, Calendar, CheckCircle2, Mail, Phone, MapPin,
    FileCheck, ChevronRight, X, Clock, Building2, CreditCard, Hash, Wallet
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { toast } from 'react-hot-toast';
import { canAccess } from '../../utils/permissions';

const getInitials = (fullName) => {
    if (!fullName) return '?';
    const parts = fullName.trim().split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return fullName.substring(0, 2).toUpperCase();
};

const getStatusBadge = (status) => {
    const statusMap = {
        'pending_company': { label: 'Pending', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
        'approved': { label: 'Approved', className: 'bg-green-100 text-green-800 border-green-300' },
        'rejected': { label: 'Rejected', className: 'bg-red-100 text-red-800 border-red-300' },
    };
    return statusMap[status] || { label: status, className: 'bg-gray-100 text-gray-800 border-gray-300' };
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) return "Just now";
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatJoinedDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};



export default function CashRequests() {
    const [selectedPayoutId, setSelectedPayoutId] = useState(null);
    const queryClient = useQueryClient();

    const canAccept = canAccess('accept_cash_request');
    const canReject = canAccess('reject_cash_request');
    const { data: payoutsData, isLoading, error, refetch } = useQuery({
        queryKey: ['companyPayouts'],
        queryFn: async () => {
            const response = await api.get('/company/payouts');
            const data = response.data.data || [];
            return data.map(payout => {
                if (typeof payout.bank_details === 'string') {
                    try {
                        payout.bank_details = JSON.parse(payout.bank_details);
                    } catch (e) {
                        console.error('Failed to parse bank_details', e);
                    }
                }
                return payout;
            });
        },
    });

    const payouts = payoutsData || [];

    const { data: payoutDetails, isLoading: isLoadingDetails } = useQuery({
        queryKey: ['companyPayout', selectedPayoutId],
        queryFn: async () => {
            const response = await api.get(`/company/payouts/${selectedPayoutId}`);
            const data = response.data.data;
            if (data && typeof data.bank_details === 'string') {
                try {
                    data.bank_details = JSON.parse(data.bank_details);
                } catch (e) {
                    console.error('Failed to parse bank_details', e);
                }
            }
            return data;
        },
        enabled: !!selectedPayoutId,
    });

    const approveMutation = useMutation({
        mutationFn: async (payoutId) => {
            return await api.put(`/company/payouts/${payoutId}/status`, {
                status: 'approved',
                notes: 'Payout approved and processed',
            });
        },
        onSuccess: (_, payoutId) => {
            toast.success('Payout approved successfully!');
            queryClient.invalidateQueries(['companyPayouts']);
            queryClient.invalidateQueries(['companyPayout', payoutId]);
            setSelectedPayoutId(null);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to approve payout');
        },
    });

    const rejectMutation = useMutation({
        mutationFn: async (payoutId) => {
            return await api.put(`/company/payouts/${payoutId}/status`, {
                status: 'rejected',
                notes: 'Payout rejected by company',
            });
        },
        onSuccess: (_, payoutId) => {
            toast.success('Payout rejected');
            queryClient.invalidateQueries(['companyPayouts']);
            queryClient.invalidateQueries(['companyPayout', payoutId]);
            setSelectedPayoutId(null);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to reject payout');
        },
    });

    const pendingCount = payouts.filter(p => p.status === 'pending_company').length;
    const totalRequested = payouts.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const averageRequest = payouts.length > 0 ? totalRequested / payouts.length : 0;
    const isProcessing = approveMutation.isPending || rejectMutation.isPending;

    if (!canAccess("view_cash_requests")) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
                    <DollarSign className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Access Restricted</h2>
                <p className="text-gray-500 max-w-md font-medium">
                    You don't have the required permissions to view cash requests.
                </p>
            </div>
        );
    }

    return (
        <>
            <div className="min-h-screen">
                <div className="bg-white border-b border-gray-200">
                    <div className="px-6 py-5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <DollarSign className="w-8 h-8 text-emerald-600" />
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">Cash Requests</h1>
                                    <p className="text-sm text-gray-600 mt-0.5">Review surveyor withdrawal requests</p>
                                </div>
                            </div>
                            <div className="text-sm text-gray-500">
                                {pendingCount} pending • USD{totalRequested.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} requested
                            </div>
                        </div>
                    </div>
                </div>

                <div className="px-6 py-5">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-emerald-100 text-xs font-medium">Pending</p>
                                    <p className="text-3xl font-bold mt-1">{pendingCount}</p>
                                </div>
                                <DollarSign className="w-9 h-9 text-white/30" />
                            </div>
                            <p className="text-emerald-100 text-xs mt-2">Awaiting approval</p>
                        </div>
                        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-blue-100 text-xs font-medium">Total Requested</p>
                                    <p className="text-3xl font-bold mt-1">
                                        USD{totalRequested.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                    </p>
                                </div>
                                <div className="text-white/30">
                                    <svg className="w-9 h-9" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.766 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.766-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                            <p className="text-blue-100 text-xs mt-2">Across all requests</p>
                        </div>
                        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-purple-100 text-xs font-medium">Average Request</p>
                                    <p className="text-3xl font-bold mt-1">
                                        USD{Math.round(averageRequest).toLocaleString()}
                                    </p>
                                </div>
                                <div className="text-white/30">
                                    <svg className="w-9 h-9" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.07l.146-.146a.5.5 0 01.708 0L8 16.07l.646-.646a.5.5 0 01.708 0L10.5 16.5l.646-.646a.5.5 0 01.708 0L13 16.07l.646-.646a.5.5 0 01.708 0L15.07 16H17a2 2 0 002-2V6a1 1 0 100-2H3z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                            <p className="text-purple-100 text-xs mt-2">Per surveyor</p>
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {isLoading && (
                    <div className="p-5">
                        <div className="bg-white rounded-xl shadow p-8 text-center">
                            <p className="text-gray-600">Loading payouts...</p>
                        </div>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="p-5">
                        <div className="bg-white rounded-xl shadow p-8 text-center">
                            <p className="text-red-600 mb-4">Failed to load payouts</p>
                            <button
                                onClick={() => refetch()}
                                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition"
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                )}

                {/* Payouts List */}
                {!isLoading && !error && (
                    <div className="p-5 space-y-4">
                        {payouts.length === 0 ? (
                            <div className="bg-white rounded-xl shadow p-8 text-center">
                                <p className="text-gray-600">No payout requests found</p>
                            </div>
                        ) : (
                            payouts.map((payout) => {
                                const statusBadge = getStatusBadge(payout.status);
                                return (
                                    <div
                                        key={payout.id}
                                        onClick={() => setSelectedPayoutId(payout.id)}
                                        className="bg-white rounded-xl shadow hover:shadow-lg transition-all duration-200 cursor-pointer border border-gray-200 hover:border-emerald-400 group mb-4"
                                    >
                                        <div className="p-5 flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow">
                                                    {getInitials(payout.surveyor?.full_name)}
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-semibold text-gray-900">{payout.surveyor?.full_name || 'Unknown'}</h3>
                                                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${statusBadge.className}`}>
                                                            {statusBadge.label}
                                                        </span>
                                                        {payout.bank_details?.method && (
                                                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1">
                                                                <Wallet className="w-3 h-3" />
                                                                {payout.bank_details.method}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                                                        <span>{payout.jobs?.length || 0} job(s)</span>
                                                        <span>•</span>
                                                        <span>{formatDate(payout.created_at)}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="text-right">
                                                <div className="text-2xl font-bold text-emerald-600">
                                                    USD{parseFloat(payout.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-1">
                                                    {payout.jobs?.length || 0} job(s) in this payout
                                                </div>
                                                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-emerald-600 transition mt-2 ml-auto" />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>

            {selectedPayoutId && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedPayoutId(null)}>
                    <div
                        className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[92vh] overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-8 py-6 text-white">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div>
                                        <h2 className="text-2xl font-bold">Cash Request #{selectedPayoutId}</h2>
                                        <p className="text-emerald-100 mt-1">
                                            {isLoadingDetails ? 'Loading...' : `Requested by ${payoutDetails?.surveyor?.full_name || 'Unknown'}`}
                                        </p>
                                    </div>
                                    {payoutDetails && (
                                        <span className="px-3 py-1 text-sm font-medium rounded-full border text-white bg-white/20 border-white/30">
                                            {getStatusBadge(payoutDetails.status).label}
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={() => setSelectedPayoutId(null)}
                                    className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        <div className="p-8 overflow-y-auto max-h-[calc(92vh-140px)] scrollbar-hide">
                            {isLoadingDetails ? (
                                <div className="text-center py-12">
                                    <p className="text-gray-600">Loading payout details...</p>
                                </div>
                            ) : payoutDetails ? (
                                <>
                                    <div className="text-center py-8 bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-2xl border-2 border-emerald-200 mb-8">
                                        <p className="text-gray-600 text-sm font-medium">Total Amount Requested</p>
                                        <p className="text-5xl font-bold text-emerald-700 mt-3">
                                            USD{parseFloat(payoutDetails.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </p>
                                        <p className="text-gray-600 text-sm mt-3">
                                            From {payoutDetails.jobs?.length || 0} job(s)
                                        </p>
                                    </div>

                                    <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200 mb-8">
                                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                            <User className="w-5 h-5 text-emerald-600" />
                                            Surveyor Details
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="flex items-center gap-4">
                                                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                                                    {getInitials(payoutDetails.surveyor?.full_name)}
                                                </div>
                                                <div>
                                                    <h4 className="text-xl font-bold text-gray-900">{payoutDetails.surveyor?.full_name || 'Unknown'}</h4>
                                                    {payoutDetails.surveyor?.role && (
                                                        <p className="text-sm text-gray-600 mt-1">{payoutDetails.surveyor.role}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="space-y-3 text-sm">
                                                {payoutDetails.surveyor?.phone && (
                                                    <div className="flex items-center gap-3">
                                                        <Phone className="w-4 h-4 text-gray-500" />
                                                        <span>{payoutDetails.surveyor.phone}</span>
                                                    </div>
                                                )}
                                                {payoutDetails.surveyor?.email && (
                                                    <div className="flex items-center gap-3">
                                                        <Mail className="w-4 h-4 text-gray-500" />
                                                        <span>{payoutDetails.surveyor.email}</span>
                                                    </div>
                                                )}
                                                {payoutDetails.surveyor?.location && (
                                                    <div className="flex items-center gap-3">
                                                        <MapPin className="w-4 h-4 text-gray-500" />
                                                        <span>{payoutDetails.surveyor.location}</span>
                                                    </div>
                                                )}
                                                {payoutDetails.surveyor?.created_at && (
                                                    <div className="flex items-center gap-3">
                                                        <Calendar className="w-4 h-4 text-gray-500" />
                                                        <span>Joined {formatJoinedDate(payoutDetails.surveyor.created_at)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {payoutDetails.bank_details && Object.keys(payoutDetails.bank_details).length > 0 && (
                                        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200 mb-8">
                                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                                <CreditCard className="w-5 h-5 text-emerald-600" />
                                                Payment Details
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
                                                {payoutDetails.bank_details.method && (
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                                                            <Wallet className="w-4 h-4 text-emerald-600" />
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-gray-500">Method</p>
                                                            <p className="font-medium text-gray-900">{payoutDetails.bank_details.method}</p>
                                                        </div>
                                                    </div>
                                                )}
                                                {payoutDetails.bank_details.phone_number && (
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                                                            <Phone className="w-4 h-4 text-blue-600" />
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-gray-500">Phone Number</p>
                                                            <p className="font-medium text-gray-900">{payoutDetails.bank_details.phone_number}</p>
                                                        </div>
                                                    </div>
                                                )}
                                                {payoutDetails.bank_details.email && (
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                                                            <Mail className="w-4 h-4 text-purple-600" />
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-gray-500">Email Address</p>
                                                            <p className="font-medium text-gray-900">{payoutDetails.bank_details.email}</p>
                                                        </div>
                                                    </div>
                                                )}
                                                {payoutDetails.bank_details.full_name && (
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                                                            <User className="w-4 h-4 text-orange-600" />
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-gray-500">Account Holder</p>
                                                            <p className="font-medium text-gray-900">{payoutDetails.bank_details.full_name}</p>
                                                        </div>
                                                    </div>
                                                )}
                                                {payoutDetails.bank_details.bank_name && (
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                                                            <Building2 className="w-4 h-4 text-indigo-600" />
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-gray-500">Bank Name</p>
                                                            <p className="font-medium text-gray-900">{payoutDetails.bank_details.bank_name}</p>
                                                        </div>
                                                    </div>
                                                )}
                                                {payoutDetails.bank_details.account_number && (
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                                                            <Hash className="w-4 h-4 text-slate-600" />
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-gray-500">Account Number</p>
                                                            <p className="font-medium text-gray-900">{payoutDetails.bank_details.account_number}</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
                                            <FileCheck className="w-5 h-5 text-emerald-600" />
                                            Jobs in this Payout ({payoutDetails.jobs?.length || 0})
                                        </h3>
                                        <div className="grid gap-5">
                                            {payoutDetails.jobs && payoutDetails.jobs.length > 0 ? (
                                                payoutDetails.jobs.map((job) => (
                                                    <div key={job.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition">
                                                        <div className="p-5">
                                                            <div className="flex items-start justify-between">
                                                                <div className="flex-1">
                                                                    <p className="font-bold text-gray-900">{job.job_number}</p>
                                                                    {job.start_date && (
                                                                        <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                                                                            <Calendar className="w-4 h-4" />
                                                                            {new Date(job.start_date).toLocaleDateString('en-US', {
                                                                                month: 'short',
                                                                                day: 'numeric',
                                                                                year: 'numeric'
                                                                            })}
                                                                            {job.start_time && ` at ${job.start_time}`}
                                                                        </p>
                                                                    )}
                                                                    {job.repeats && (
                                                                        <p className="text-xs text-gray-500 mt-1">{job.repeats}</p>
                                                                    )}
                                                                    {job.visit_instructions && (
                                                                        <p className="text-xs text-gray-600 mt-2 italic">"{job.visit_instructions}"</p>
                                                                    )}
                                                                </div>
                                                                <div className="text-right ml-4">
                                                                    <p className="text-2xl font-bold text-emerald-600">
                                                                        USD{parseFloat(job.pivot?.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="bg-white rounded-xl border border-gray-200 p-5 text-center text-gray-500">
                                                    No jobs found in this payout
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-4 pt-8 border-t-2 border-gray-200 mt-8">
                                        <button
                                            onClick={() => setSelectedPayoutId(null)}
                                            disabled={isProcessing}
                                            className="px-8 py-3.5 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Cancel
                                        </button>
                                        {payoutDetails.status === 'pending_company' ? (
                                            (() => {
                                                const canReject = canAccess('reject_cash_request');
                                                const canApprove = canAccess('accept_cash_request');
                                                const showButtons = canReject || canApprove;

                                                return (
                                                    <>
                                                        {canReject && (
                                                            <button
                                                                onClick={() => rejectMutation.mutate(selectedPayoutId)}
                                                                disabled={isProcessing}
                                                                className="px-8 py-3.5 border-2 border-red-500 text-red-600 rounded-xl font-medium hover:bg-red-50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                                            >
                                                                {rejectMutation.isPending && <Clock className="w-4 h-4 animate-spin" />}
                                                                Reject Request
                                                            </button>
                                                        )}
                                                        {canApprove && (
                                                            <button
                                                                onClick={() => approveMutation.mutate(selectedPayoutId)}
                                                                disabled={isProcessing}
                                                                className="px-10 py-3.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                {approveMutation.isPending ? (
                                                                    <>
                                                                        <Clock className="w-6 h-6 animate-spin" />
                                                                        Processing...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <CheckCircle2 className="w-6 h-6" />
                                                                        Approve & Transfer
                                                                    </>
                                                                )}
                                                            </button>
                                                        )}
                                                        {!showButtons && (
                                                            <div className="text-sm text-gray-500 flex items-center">
                                                                You don't have permission to take action on this request.
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            })()
                                        ) : (
                                            <div className="text-sm text-gray-500 flex items-center">
                                                This payout has already been {payoutDetails.status === 'completed' || 'pending_admin' ? 'approved' : 'rejected'}
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-12">
                                    <p className="text-red-600">Failed to load payout details</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}